import React, { useState, useEffect } from 'react';
import { AlertCircle, Save, Bell, Palette } from 'lucide-react';
import { NotificationManager } from 'react-notifications';
import { useSettings } from '../../../hooks/useDashboard';

/**
 * ManagerSettings Component
 * Account preferences for property managers - theme and notification
 * preferences, backed by the same settings endpoint used account-wide.
 */
const ManagerSettings = () => {
    const { settings, loading, error, updating, updateSettings } = useSettings();
    const [formData, setFormData] = useState({
        theme: 'light',
        emailNotifications: true,
        smsNotifications: false,
        pushNotifications: true
    });

    useEffect(() => {
        if (settings) {
            setFormData({
                theme: settings.appPreferences?.theme || 'light',
                emailNotifications: settings.notificationPreferences?.email !== false,
                smsNotifications: settings.notificationPreferences?.sms === true,
                pushNotifications: settings.notificationPreferences?.push !== false
            });
        }
    }, [settings]);

    const handleSave = async () => {
        const updates = {
            theme: formData.theme,
            notificationPreferences: {
                email: formData.emailNotifications,
                sms: formData.smsNotifications,
                push: formData.pushNotifications
            }
        };

        const result = await updateSettings(updates);

        if (result.success) {
            NotificationManager.success('Settings saved successfully!', 'Success');
        } else {
            NotificationManager.error(result.error || 'Failed to save settings', 'Error');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-highland-500 mx-auto mb-4"></div>
                    <p className="text-soil-600">Loading settings...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-karibu-100">
                <div className="text-center bg-white p-8 rounded-lg shadow-md max-w-md">
                    <AlertCircle className="h-12 w-12 text-murram-500 mx-auto mb-4" />
                    <h2 className="text-xl font-semibold text-soil-900 mb-2">Error Loading Settings</h2>
                    <p className="text-soil-600 mb-4">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 bg-highland-500 text-white rounded-lg hover:bg-highland-600"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-karibu-100 pt-20 pb-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <h1 className="font-display text-3xl font-bold text-soil-900">Manager Settings</h1>
                    <p className="mt-2 text-soil-600">
                        Manage your account preferences
                    </p>
                </div>

                <div className="space-y-6">
                    {/* Appearance */}
                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center mb-4">
                            <Palette className="h-5 w-5 text-soil-400 mr-2" />
                            <h2 className="text-xl font-semibold text-soil-900">Appearance</h2>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-soil-600 mb-2">
                                Theme
                            </label>
                            <select
                                value={formData.theme}
                                onChange={(e) => setFormData({ ...formData, theme: e.target.value })}
                                className="w-full px-4 py-2 border border-soil-100 rounded-lg focus:outline-none focus:ring-2 focus:ring-highland-500"
                            >
                                <option value="light">Light</option>
                                <option value="dark">Dark</option>
                                <option value="auto">Auto</option>
                            </select>
                        </div>
                    </div>

                    {/* Notifications */}
                    <div className="bg-white rounded-lg shadow-sm p-6 border border-soil-100">
                        <div className="flex items-center mb-4">
                            <Bell className="h-5 w-5 text-soil-400 mr-2" />
                            <h2 className="text-xl font-semibold text-soil-900">Notification Preferences</h2>
                        </div>
                        <div className="space-y-4">
                            <label className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    checked={formData.emailNotifications}
                                    onChange={(e) => setFormData({ ...formData, emailNotifications: e.target.checked })}
                                    className="w-5 h-5 text-highland-500 border-soil-100 rounded focus:ring-highland-500"
                                />
                                <div>
                                    <p className="font-medium text-soil-900">Email Notifications</p>
                                    <p className="text-sm text-soil-600">New applications, messages, and account updates</p>
                                </div>
                            </label>
                            <label className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    checked={formData.smsNotifications}
                                    onChange={(e) => setFormData({ ...formData, smsNotifications: e.target.checked })}
                                    className="w-5 h-5 text-highland-500 border-soil-100 rounded focus:ring-highland-500"
                                />
                                <div>
                                    <p className="font-medium text-soil-900">SMS Notifications</p>
                                    <p className="text-sm text-soil-600">Urgent alerts sent via text message</p>
                                </div>
                            </label>
                            <label className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    checked={formData.pushNotifications}
                                    onChange={(e) => setFormData({ ...formData, pushNotifications: e.target.checked })}
                                    className="w-5 h-5 text-highland-500 border-soil-100 rounded focus:ring-highland-500"
                                />
                                <div>
                                    <p className="font-medium text-soil-900">Push Notifications</p>
                                    <p className="text-sm text-soil-600">Receive browser notifications</p>
                                </div>
                            </label>
                        </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex items-center justify-end space-x-4">
                        <button
                            onClick={handleSave}
                            disabled={updating}
                            className="flex items-center px-6 py-3 bg-highland-500 text-white rounded-lg font-medium hover:bg-highland-600 disabled:opacity-50 transition-colors"
                        >
                            {updating ? (
                                <>
                                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                    Saving...
                                </>
                            ) : (
                                <>
                                    <Save className="h-4 w-4 mr-2" />
                                    Save Changes
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ManagerSettings;
